/* Produktseite: Varianten, Selling Plans, Galerie, Sticky ATC, Fütterungsrechner */
const { formatMoney, showToast } = window.themeHelpers;
const { CartAPI, RecentlyViewed } = window;

class ProductInfo extends HTMLElement {
  connectedCallback() {
    try {
      this.variants = JSON.parse(this.querySelector('[data-variant-data]').textContent);
    } catch (e) {
      this.variants = [];
    }
    this.form = this.querySelector('form[data-type="add-to-cart-form"], form[action*="/cart/add"]');
    this.variantInput = this.querySelector('[data-variant-id]');
    this.sellingPlanInput = this.querySelector('[data-selling-plan-input]');
    this.currentVariant = this.variants.find((v) => String(v.id) === this.variantInput?.value) || this.variants[0];

    this.initVariantPicker();
    this.initSellingPlans();
    this.initGallery();
    this.initZoom();
    this.initStickyAtc();
    this.registerRecentlyViewed();
  }

  /* ----- Varianten ----- */
  initVariantPicker() {
    const picker = this.querySelector('[data-variant-selects]');
    if (!picker) return;
    picker.addEventListener('change', () => {
      const selected = [];
      picker.querySelectorAll('fieldset').forEach((fs) => {
        const checked = fs.querySelector('input:checked');
        if (checked) selected.push(checked.value);
      });
      const match = this.variants.find((v) => v.options.every((opt, i) => opt === selected[i]));
      picker.querySelectorAll('fieldset').forEach((fs, i) => {
        const label = fs.querySelector(`[data-selected-option="${i}"]`);
        if (label) label.textContent = selected[i] || '';
      });
      this.setVariant(match);
    });
  }

  setVariant(variant) {
    const addButton = this.querySelector('[data-add-button]');
    const atcText = this.querySelector('[data-add-button] [data-atc-text]');
    const backorderNote = this.querySelector('[data-backorder-note]');
    if (!variant) {
      addButton?.setAttribute('disabled', '');
      if (atcText) atcText.textContent = window.themeStrings.unavailable;
      return;
    }
    this.currentVariant = variant;
    if (this.variantInput) this.variantInput.value = variant.id;

    const url = new URL(window.location.href);
    url.searchParams.set('variant', variant.id);
    history.replaceState({}, '', url);

    if (addButton) {
      addButton.toggleAttribute('disabled', !variant.available);
      if (atcText) atcText.textContent = variant.available ? window.themeStrings.addToCart : window.themeStrings.soldOut;
    }
    if (backorderNote) backorderNote.toggleAttribute('hidden', variant.available);

    const priceCurrent = this.querySelector('[data-price-current]');
    if (priceCurrent) priceCurrent.textContent = formatMoney(variant.price);
    const priceCompare = this.querySelector('[data-price-compare]');
    if (priceCompare) {
      const onSale = variant.compare_at_price && variant.compare_at_price > variant.price;
      priceCompare.toggleAttribute('hidden', !onSale);
      const s = priceCompare.querySelector('s');
      if (onSale && s) s.textContent = formatMoney(variant.compare_at_price);
      priceCompare.closest('.price')?.classList.toggle('price--on-sale', !!onSale);
    }
    const priceUnit = this.querySelector('[data-price-unit]');
    if (priceUnit) {
      if (variant.unit_price && variant.unit_ref) {
        priceUnit.removeAttribute('hidden');
        priceUnit.textContent = `${formatMoney(variant.unit_price)}/${variant.unit_ref}`;
      } else {
        priceUnit.setAttribute('hidden', '');
      }
    }
    const oneTimePrice = this.querySelector('[data-onetime-price]');
    if (oneTimePrice) oneTimePrice.textContent = formatMoney(variant.price);
    const stickyPrice = this.querySelector('[data-sticky-price]');
    if (stickyPrice) stickyPrice.textContent = formatMoney(variant.price);
    const stickyAdd = this.querySelector('[data-sticky-add]');
    if (stickyAdd) stickyAdd.toggleAttribute('disabled', !variant.available);

    this.updatePlanPrice();
    if (variant.media_id) this.scrollToMedia(String(variant.media_id));
  }

  /* ----- Selling Plans (Subscribe & Save) ----- */
  initSellingPlans() {
    const options = this.querySelectorAll('[data-purchase-option]');
    if (!options.length) return;
    options.forEach((radio) => {
      radio.addEventListener('change', () => this.syncSellingPlan());
    });
    this.querySelectorAll('[data-selling-plan-select]').forEach((select) => {
      select.addEventListener('change', () => this.syncSellingPlan());
    });
    this.syncSellingPlan();
  }
  syncSellingPlan() {
    const checked = this.querySelector('[data-purchase-option]:checked');
    if (!checked || !this.sellingPlanInput) return;
    if (checked.value === '') {
      this.sellingPlanInput.value = '';
    } else {
      const body = checked.closest('.purchase-option');
      const select = body?.querySelector('[data-selling-plan-select]');
      this.sellingPlanInput.value = select ? select.value : checked.value;
    }
    this.updatePlanPrice();
  }
  updatePlanPrice() {
    this.querySelectorAll('.purchase-option').forEach((option) => {
      const select = option.querySelector('[data-selling-plan-select]');
      const priceEl = option.querySelector('[data-plan-price]');
      if (!select || !priceEl || !this.currentVariant) return;
      const planPrice = this.currentVariant.plans?.[select.value];
      priceEl.textContent = planPrice != null ? formatMoney(planPrice) : '';
    });
  }

  /* ----- Galerie ----- */
  initGallery() {
    this.slides = this.querySelector('[data-gallery-slides]');
    const thumbs = this.querySelectorAll('[data-thumb-for]');
    if (!this.slides) return;
    thumbs.forEach((thumb) => {
      thumb.addEventListener('click', () => this.scrollToMedia(thumb.dataset.thumbFor));
    });
    this.slides.addEventListener('scroll', () => {
      clearTimeout(this._scrollTimer);
      this._scrollTimer = setTimeout(() => {
        const index = Math.round(this.slides.scrollLeft / this.slides.clientWidth);
        const slide = this.slides.children[index];
        if (!slide) return;
        thumbs.forEach((t) => t.setAttribute('aria-current', t.dataset.thumbFor === slide.dataset.mediaId ? 'true' : 'false'));
      }, 80);
    }, { passive: true });
  }
  scrollToMedia(mediaId) {
    const slide = this.slides?.querySelector(`[data-media-id="${mediaId}"]`);
    if (!slide) return;
    this.slides.scrollTo({ left: slide.offsetLeft, behavior: 'smooth' });
  }

  /* ----- Zoom ----- */
  initZoom() {
    const modal = this.querySelector('[data-media-zoom]');
    if (!modal) return;
    const target = modal.querySelector('[data-zoom-target]');
    this.addEventListener('click', (e) => {
      const img = e.target.closest('[data-zoomable]');
      if (img) {
        target.src = img.dataset.zoomSrc;
        target.alt = img.alt;
        modal.classList.add('is-open');
        modal.querySelector('[data-zoom-close]').focus();
      }
      if (e.target.closest('[data-zoom-close]') || e.target === modal) {
        modal.classList.remove('is-open');
      }
    });
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') modal.classList.remove('is-open');
    });
  }

  /* ----- Sticky Add-to-Cart (Mobile) ----- */
  initStickyAtc() {
    const bar = this.querySelector('[data-sticky-atc]');
    const mainButton = this.querySelector('[data-add-button]');
    if (!bar || !mainButton) return;
    const observer = new IntersectionObserver(([entry]) => {
      const visible = !entry.isIntersecting && entry.boundingClientRect.top < 0;
      bar.classList.toggle('is-visible', visible);
      bar.setAttribute('aria-hidden', visible ? 'false' : 'true');
    }, { threshold: 0 });
    observer.observe(mainButton);
    bar.querySelector('[data-sticky-add]')?.addEventListener('click', () => {
      this.querySelector('product-form form')?.requestSubmit();
    });
  }

  /* ----- Recently Viewed registrieren ----- */
  registerRecentlyViewed() {
    try {
      const meta = JSON.parse(this.querySelector('[data-product-meta]').textContent);
      RecentlyViewed.add(meta);
    } catch (e) { /* ignorieren */ }
  }
}
if (!customElements.get('product-info')) customElements.define('product-info', ProductInfo);

/* ----- Fütterungsrechner ----- */
class FeedingCalculator extends HTMLElement {
  connectedCallback() {
    this.factor = parseFloat(this.dataset.factor);
    this.input = this.querySelector('[data-feeding-weight]');
    this.result = this.querySelector('[data-feeding-result]');
    this.querySelector('[data-feeding-calc]')?.addEventListener('click', () => this.calculate());
    this.input?.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') { e.preventDefault(); this.calculate(); }
    });
  }
  calculate() {
    const weight = parseFloat(this.input.value);
    if (!weight || weight <= 0 || !this.factor) {
      this.result.hidden = true;
      return;
    }
    const grams = Math.round(weight * this.factor);
    this.result.innerHTML = window.themeStrings.feedingResult
      .replace('[weight]', weight.toLocaleString('de-DE'))
      .replace('[amount]', `<strong>${grams} g</strong>`);
    this.result.hidden = false;
  }
}
if (!customElements.get('feeding-calculator')) customElements.define('feeding-calculator', FeedingCalculator);

/* ----- „Beides in den Warenkorb“ (Bundle-Offer) ----- */
document.addEventListener('click', async (e) => {
  const btn = e.target.closest('[data-bundle-add]');
  if (!btn) return;
  e.preventDefault();
  const productInfo = btn.closest('product-info');
  const mainVariantId = productInfo?.querySelector('[data-variant-id]')?.value;
  const companionId = btn.dataset.bundleAdd;
  btn.setAttribute('aria-disabled', 'true');
  try {
    const items = [{ id: parseInt(companionId, 10), quantity: 1 }];
    if (mainVariantId) items.unshift({ id: parseInt(mainVariantId, 10), quantity: 1 });
    await CartAPI.add({ items });
  } catch (err) {
    showToast(err.message);
  } finally {
    btn.removeAttribute('aria-disabled');
  }
});

/* ----- Produktempfehlungen (Recommendations API + Section Rendering) ----- */
class ProductRecommendations extends HTMLElement {
  connectedCallback() {
    /* Direkt laden: Das Element ist anfangs leer (0 px hoch), ein IntersectionObserver
       feuert darauf nicht zuverlässig – und der eine Request ist günstig. */
    this.load();
  }
  async load() {
    try {
      const response = await fetch(this.dataset.url);
      const text = await response.text();
      const parsed = new DOMParser().parseFromString(text, 'text/html');
      const inner = parsed.querySelector('product-recommendations');
      if (inner && inner.innerHTML.trim().length) {
        this.innerHTML = inner.innerHTML;
      } else {
        this.closest('.shopify-section')?.remove();
      }
    } catch (e) {
      this.closest('.shopify-section')?.remove();
    }
  }
}
if (!customElements.get('product-recommendations')) customElements.define('product-recommendations', ProductRecommendations);

/* ----- Zuletzt angesehen (sessionStorage) ----- */
class RecentlyViewedList extends HTMLElement {
  connectedCallback() {
    const items = RecentlyViewed.get().filter((p) => p.handle !== this.dataset.excludeHandle).slice(0, parseInt(this.dataset.limit || 4, 10));
    if (!items.length) {
      this.closest('.shopify-section')?.remove();
      return;
    }
    const list = this.querySelector('[data-recently-viewed-list]');
    if (!list) return;
    list.innerHTML = items.map((p) => `
      <div role="listitem">
        <article class="product-card">
          <div class="product-card__media" style="aspect-ratio: 1;">
            ${p.image ? `<img class="product-card__img product-card__img--main" src="${p.image}" alt="" width="540" height="540" loading="lazy">` : ''}
          </div>
          <div class="product-card__content">
            <h3 class="product-card__title"><a href="${p.url}">${p.title}</a></h3>
            <div class="product-card__price"><div class="price"><span class="price__current">${formatMoney(p.price)}</span></div></div>
          </div>
        </article>
      </div>`).join('');
    this.hidden = false;
  }
}
if (!customElements.get('recently-viewed-list')) customElements.define('recently-viewed-list', RecentlyViewedList);
